import { getProblemData } from "store/functions/openapi_conversion_functions";
import { scenarioSpansMultipleDays } from "store/functions/multiDay";
import { getCoordinateSystem } from "store/functions/coordinateSystem.js";
import TimeSettings from "open-api-gen/model/TimeSettings.js";
import ProblemRequest from "open-api-gen/model/ProblemRequest.js";

import generic_backend_request_post from 'functions/server/generic_backend_request_post';
import { isUncostedTravelCostMatrix, UNCOSTED_MATRIX_MESSAGE } from "store/functions/travelCostResults.js";

/**
 * Builds the ProblemRequest payload from a scenario state snapshot.
 *
 * Pure and synchronous, so the payload can be captured at enqueue time: a queued
 * run then solves the scenario exactly as it was when the user clicked Run,
 * regardless of edits made while it waits for a slot.
 *
 * @param {Object} state - inputData.present (data + generalSettings + activeModelType).
 * @returns {ProblemRequest} The formatted problem to submit.
 * @throws {Error} If required scenario data is missing/invalid.
 */
export function buildOptimizationProblem(state) {
    if (!state) {
        throw new Error('State is required to build an optimization problem');
    }
    if (!state.data) {
        throw new Error('State data is required to build an optimization problem');
    }
    if (!state.generalSettings) {
        throw new Error('State generalSettings is required to build an optimization problem');
    }

    const timeSettings = state.generalSettings.time;
    const problemData = getProblemData(state);
    // `multipleDays` is inferred from the data (any *DayInt > 0), not read from
    // the `timePeriodSpansDays` UI toggle, which drifts across scenario loads and
    // caused single-day solves to be flagged as the multi-day premium feature
    // (and multi-day scenarios loaded with the toggle off to lose their day
    // constraints). See store/functions/multiDay.
    const problemTimeSettings = TimeSettings.constructFromObject({
        timeUnit: timeSettings.timeUnit,
        timeFormat: "HH:mm:ss",
        multipleDays: scenarioSpansMultipleDays(problemData),
        dateUnit: timeSettings.dayUnit,
        dateFormat: "M/d/yyyy"
    });

    return ProblemRequest.constructFromObject({
        problemType: state.activeModelType,
        problemData: problemData,
        timeSettings: problemTimeSettings,
        // Must be declared, not left to the backend default. getProblemData
        // nulls latitude/longitude on a cartesian scenario and sends x/y
        // instead; without this field the solver assumes 'geographic', finds
        // plane coordinates, and refuses the run outright:
        //   "coordinateSystem is 'geographic', which expects
        //    latitude/longitude, but 103 location(s) supply x/y"
        // Always sent, including 'geographic' — the state is the authority on
        // this, and an explicit value cannot drift from the payload it
        // describes the way an omitted one silently did.
        coordinateSystem: getCoordinateSystem(state)
    });
}

/**
 * Submits a pre-built problem to the backend for solving.
 *
 * Asynchronous: resolves immediately with { run_id, status: 'queued' } — it does
 * NOT wait for the solve. The run is then polled via /run_status and its results
 * loaded via /run_results_by_run_id once it succeeds.
 *
 * @param {ProblemRequest} problem - a payload from buildOptimizationProblem.
 * @param {string} userId - the id of the user making the request.
 */
export async function submit_problem(problem, userId, exampleSlug = null) {
    if (!problem) {
        throw new Error('Problem is required to submit an optimization request');
    }
    if (!userId) {
        throw new Error('User ID is required to submit an optimization request');
    }
    // Last line of defence, for any caller that reaches /problem without going
    // through enqueueOptimization's check: never send a matrix with no numbers in
    // it. The backend solves it and reports success over meaningless routes.
    if (isUncostedTravelCostMatrix(problem.problemData && problem.problemData.travelCosts)) {
        throw new Error(UNCOSTED_MATRIX_MESSAGE);
    }
    // exampleSlug (optional): identifies a solve of a published example so the
    // backend applies that example's entitlement allowance instead of the
    // caller's tier caps. Omitted entirely for the user's own scenarios.
    return generic_backend_request_post(
        { problem, userId, ...(exampleSlug ? { exampleSlug } : {}) },
        '/problem'
    );
}
